import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";

const PLANS = [
  {
    name: "Starter",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "14-day free trial, no card needed",
    features: ["Up to 50 members", "1 branch location", "Basic attendance tracking", "Standard reports", "Email support"],
    cta: "Start Free Trial",
    ctaLink: "/registration-page",
    highlight: false,
  },
  {
    name: "Growth",
    monthlyPrice: 29,
    annualPrice: 23,
    description: "For gyms scaling up fast",
    features: ["Up to 500 members", "5 branch locations", "QR code check-ins", "Advanced analytics", "Priority support", "Automated billing"],
    cta: "Get Started",
    ctaLink: "/registration-page",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    monthlyPrice: 79,
    annualPrice: 63,
    description: "For multi-chain gym networks",
    features: ["Unlimited members", "Unlimited branches", "Custom integrations", "White-label option", "Dedicated account manager", "SLA guarantee"],
    cta: "Contact Sales",
    ctaLink: "/registration-page",
    highlight: false,
  },
];

export default function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 px-4 bg-slate-900/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-teal-400 text-sm font-semibold font-caption uppercase tracking-wider">Pricing</span>
          <h2 className="text-4xl sm:text-5xl font-bold font-heading mt-3 mb-4">Simple, transparent pricing</h2>
          <p className="text-slate-400 font-body max-w-xl mx-auto mb-8">
            No hidden fees. Cancel anytime. Scale as you grow.
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center gap-3 bg-slate-800/60 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-lg text-sm font-caption font-medium transition-all ${
                !annual ? "bg-primary text-white shadow-md" : "text-slate-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-lg text-sm font-caption font-medium transition-all flex items-center gap-2 ${
                annual ? "bg-primary text-white shadow-md" : "text-slate-400 hover:text-white"
              }`}
            >
              Annual
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">-20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {PLANS?.map((plan) => (
            <div
              key={plan?.name}
              className={`relative rounded-2xl p-7 border transition-all duration-300 ${
                plan?.highlight
                  ? "bg-primary/10 border-primary/40 shadow-xl shadow-teal-900/20 scale-[1.02]"
                  : "bg-slate-900/60 border-white/5 hover:border-white/10"
              }`}
            >
              {plan?.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-white text-xs font-semibold font-caption shadow-lg">
                  {plan?.badge}
                </div>
              )}

              <h3 className="text-lg font-bold text-white font-heading mb-1">{plan?.name}</h3>
              <p className="text-slate-500 text-sm font-body mb-5">{plan?.description}</p>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-white font-heading">
                  ${annual ? plan?.annualPrice : plan?.monthlyPrice}
                </span>
                {plan?.monthlyPrice > 0 && (
                  <span className="text-slate-500 text-sm font-body">/month</span>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan?.features?.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300 font-body">
                    <div className="w-4 h-4 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={10} color="#2dd4bf" />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to={plan?.ctaLink}
                className={`block text-center py-3 rounded-xl text-sm font-semibold font-caption transition-all duration-200 ${
                  plan?.highlight
                    ? "bg-primary hover:bg-primary/90 text-white shadow-lg shadow-teal-900/30"
                    : "border border-white/10 hover:border-white/20 text-white hover:bg-white/5"
                }`}
              >
                {plan?.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
